import React from 'react';
import ReactTooltip from 'react-tooltip';
import '@szhsin/react-menu/dist/index.css';
import { EyeIcon } from '@heroicons/react/24/outline';
import { formatterPeso } from '../../../../utils/format';
import { SiesaLogIcon } from '../Table/SiesaLogIcon';
import { store } from '../../../../store';
import { setSelSolicitud, setSelTipoVehiculo } from '../../../../actions';

const ToolTipWrap = ({ itemID, componentPrefix, message, children }) => {
  return (
    <>
      <ReactTooltip
        className="margin-10"
        id={`${componentPrefix}${itemID}`}
        type="dark"
        effect="float">
        <p>{message}</p>
      </ReactTooltip>
      {children}
    </>
  );
};

const buildSolicitudFromHistoryRow = (item) => ({
  SOLICITUD_ID: item.SOLICITUD_ID,
  SOLICITUD_CONSECUTIVO_INT: item.SOLICITUD_CONSECUTIVO_INT,
  SOLICITUD_CONSECUTIVO: item.SOLICITUD_CONSECUTIVO,
  SOLICITUD: item.SOLICITUD,
  SOLICITUD_DESCRIPCION: item.SOLICITUD_DESCRIPCION,
  SOLICITUD_ESTADO_DESCRIPCION: item.SOLICITUD_ESTADO_DESCRIPCION,
  ORIGEN: item.ORIGEN,
  DESTINO: item.DESTINO,
  SOLICITUD_FECHA: item.SOLICITUD_FECHA || item.ANTICIPO_CREATED_AT,
  ORDEN_OPERACION_CONSECUTIVO: item.ORDEN_OPERACION_CONSECUTIVO,
  PREFACTURA_CONSECUTIVO: item.PREFACTURA_CONSECUTIVO,
  unidad_negocio: item.NEGOCIO_NOMBRE ? [{ NEGOCIO_NOMBRE: item.NEGOCIO_NOMBRE }] : [],
});

const buildTipoVehiculoFromHistoryRow = (item) => ({
  SOLICITUD_VEHICULO_TIPO_ID: item.SOLICITUD_VEHICULO_TIPO_ID,
  VEHICULO_TIPO_DESCRIPCION: item.VEHICULO_TIPO_DESCRIPCION,
  VEHICULO_PLACA: item.VEHICULO_PLACA,
  V_VEHICULO_PROPIO: item.V_VEHICULO_PROPIO,
  V_TERCERO_TENEDOR_NOMBRE: item.V_TERCERO_TENEDOR_NOMBRE,
  V_TERCERO_TENEDOR_DOCUMENTO: item.V_TERCERO_TENEDOR_DOCUMENTO,
  V_TERCERO_PROPIETARIO_NOMBRE: item.V_TERCERO_PROPIETARIO_NOMBRE,
  CONDUCTOR_NOMBRE: item.CONDUCTOR_NOMBRE,
  CONDUCTOR_ES_EMPLEADO: item.CONDUCTOR_ES_EMPLEADO,
  CONDUCTOR_DOC: item.CONDUCTOR_DOC,
  CONDUCTOR_IDENTIFICACION: item.CONDUCTOR_IDENTIFICACION,
  APAREJADOR_NOMBRE: item.APAREJADOR_NOMBRE,
  APAREJADOR_ES_EMPLEADO: item.APAREJADOR_ES_EMPLEADO,
  APAREJADOR_DOC: item.APAREJADOR_DOC,
  APAREJADOR_IDENTIFICACION: item.APAREJADOR_IDENTIFICACION,
});

const getTrimmedValue = (value) => {
  if (value === undefined || value === null) return '';
  return `${value}`.trim();
};

const buildEmployeeStatus = (flag) => {
  if (flag === 1) {
    return { isEmployee: true, label: 'Empleado' };
  }

  if (flag === 0) {
    return { isEmployee: false, label: 'Externo' };
  }

  return { isEmployee: false, label: '...' };
};

const EmployeeLabel = ({ status }) => {
  const isEmployee = status?.isEmployee ?? false;
  const label = status?.label ?? '...';

  return <span className={`${isEmployee ? 'text-green-600' : 'text-red-600'} `}>{`${label} `}</span>;
};

const buildConductorDisplay = (data) => {
  const isExternalVehicle = Number(data.V_VEHICULO_PROPIO) === 0;
  const beneficiaryDoc = getTrimmedValue(data.BENEFICIARIO_DOC);
  const conductorDoc = getTrimmedValue(data.CONDUCTOR_DOC);
  const tenedorDoc = getTrimmedValue(data.V_TERCERO_TENEDOR_DOCUMENTO);
  const hasBeneficiaryDoc = beneficiaryDoc !== '';
  const isOwnVehicle = !isExternalVehicle;

  let displayName = getTrimmedValue(data.CONDUCTOR_NOMBRE) || data.CONDUCTOR_NOMBRE || '';
  let displayDocument = conductorDoc;
  let employeeStatus = buildEmployeeStatus(data.CONDUCTOR_ES_EMPLEADO);
  let providerTag = null;

  if (hasBeneficiaryDoc) {
    displayName = getTrimmedValue(data.BENEFICIARIO_NOMBRE) || displayName;
    displayDocument = beneficiaryDoc;
    employeeStatus = buildEmployeeStatus(data.BENEFICIARIO_ES_EMPLEADO);

    const isBeneficiaryDifferentFromTenedor = tenedorDoc !== '' && beneficiaryDoc !== tenedorDoc;
    const isBeneficiarySameAsConductor = conductorDoc !== '' && beneficiaryDoc === conductorDoc;

    if (isBeneficiaryDifferentFromTenedor && (!isOwnVehicle || !isBeneficiarySameAsConductor)) {
      providerTag = 'Proveedor';
    }
  } else if (isExternalVehicle) {
    displayName = getTrimmedValue(data.V_TERCERO_TENEDOR_NOMBRE) || displayName;
    displayDocument = tenedorDoc || displayDocument;
  }

  return {
    prefix: 'C.',
    displayName,
    displayDocument,
    employeeStatus,
    providerTag,
  };
};

const buildAparejadorDisplay = (data) => ({
  prefix: 'A.',
  displayName: getTrimmedValue(data.APAREJADOR_NOMBRE) || data.APAREJADOR_NOMBRE || '',
  displayDocument: getTrimmedValue(data.APAREJADOR_DOC),
  employeeStatus: buildEmployeeStatus(data.APAREJADOR_ES_EMPLEADO),
  providerTag: null,
});

const buildProveedorDisplay = (data) => ({
  prefix: 'P.',
  displayName:
    getTrimmedValue(data.PROVEEDOR_NOMBRE) ||
    getTrimmedValue(data.BENEFICIARIO_NOMBRE) ||
    getTrimmedValue(data.V_TERCERO_TENEDOR_NOMBRE) ||
    '',
  displayDocument:
    getTrimmedValue(data.PROVEEDOR_DOC) ||
    getTrimmedValue(data.BENEFICIARIO_DOC) ||
    getTrimmedValue(data.V_TERCERO_TENEDOR_DOCUMENTO),
  employeeStatus: buildEmployeeStatus(data.PROVEEDOR_ES_EMPLEADO ?? data.BENEFICIARIO_ES_EMPLEADO),
  providerTag: 'Proveedor',
});

const buildDefaultBeneficiaryDisplay = (data) => ({
  prefix: '',
  displayName:
    getTrimmedValue(data.BENEFICIARIO_NOMBRE) ||
    getTrimmedValue(data.CONDUCTOR_NOMBRE) ||
    getTrimmedValue(data.PROVEEDOR_NOMBRE) ||
    '',
  displayDocument:
    getTrimmedValue(data.BENEFICIARIO_DOC) ||
    getTrimmedValue(data.CONDUCTOR_DOC) ||
    getTrimmedValue(data.PROVEEDOR_DOC),
  employeeStatus: buildEmployeeStatus(data.BENEFICIARIO_ES_EMPLEADO ?? data.CONDUCTOR_ES_EMPLEADO),
  providerTag: null,
});

const getBeneficiarioDisplay = (item) => {
  switch (item?.SOLICITUD_ANTICIPO_TIPO) {
    case 'C':
      return buildConductorDisplay(item);
    case 'A':
      return buildAparejadorDisplay(item);
    case 'P':
      return buildProveedorDisplay(item);
    default:
      return buildDefaultBeneficiaryDisplay(item || {});
  }
};

const VehiclePlateCell = ({ item }) => {
  const ownershipFlag = Number(item.V_VEHICULO_PROPIO);
  const isOwnVehicle = ownershipFlag === 1;
  const isExternalVehicle = ownershipFlag === 0;
  const ownershipLabel = isOwnVehicle ? 'Propio' : isExternalVehicle ? 'Externo' : 'N/D';
  const tenedorName = isExternalVehicle ? getTrimmedValue(item.V_TERCERO_TENEDOR_NOMBRE) : '';
  const tenedorDocument = isExternalVehicle ? getTrimmedValue(item.V_TERCERO_TENEDOR_DOCUMENTO) : '';

  return (
    <td className="px-2 py-2 text-sm text-gray-900">
      <div className="flex flex-col">
        <span>
          {item.VEHICULO_PLACA || 'N/A'}
          <span className="ml-1 text-xs font-semibold text-gray-500">({ownershipLabel})</span>
        </span>
        {isExternalVehicle && tenedorName && (
          <span className="text-[10px] text-gray-500">
            Tenedor: {tenedorName.substring(0, 40)}
            {tenedorDocument && ` · Doc: ${tenedorDocument}`}
          </span>
        )}
      </div>
    </td>
  );
};

const BeneficiarioCell = ({ item }) => {
  const { prefix, displayName, displayDocument, employeeStatus, providerTag } = getBeneficiarioDisplay(item);
  const truncatedName = displayName ? displayName.substring(0, 40) : '...';

  return (
    <td className="px-2 py-2 text-sm text-gray-900">
      <div className="flex flex-col">
        <span>
          {prefix ? `${prefix} ` : ''}
          {truncatedName} <EmployeeLabel status={employeeStatus} />
          {providerTag && <span className="ml-1 text-xs font-semibold text-blue-600">{providerTag}</span>}
        </span>
        {displayDocument && <span className="text-[10px] text-gray-500">Doc: {displayDocument}</span>}
      </div>
    </td>
  );
};

const TableAnticipoTbodyFull = (props) => {
  const { anticipoFullData } = props.data || {};

  const { handleShowSiesaLog } = props.actions || {};
  const { setIsModalAnticipoConsultaVisible } = props.modals || {};

  const handleShowHistoryModal = (item) => {
    if (!item) return;

    const solicitudData = buildSolicitudFromHistoryRow(item);
    const tipoVehiculoData = buildTipoVehiculoFromHistoryRow(item);

    if (solicitudData) {
      store.dispatch(setSelSolicitud(solicitudData));
    }

    if (tipoVehiculoData) {
      store.dispatch(setSelTipoVehiculo(tipoVehiculoData));
    }

    if (setIsModalAnticipoConsultaVisible) {
      setIsModalAnticipoConsultaVisible(true);
    }
  };

  const items = anticipoFullData?.data ?? [];

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Formato manual dd/MM/yyyy HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  // Función para formatear la solicitud con los códigos adicionales
  const getFormattedSolicitud = (item) => {
    let solicitud = item.SOLICITUD_CONSECUTIVO_INT || 'N/A';

    // Añadir ORDEN_OPERACION_CONSECUTIVO si existe
    if (item.ORDEN_OPERACION_CONSECUTIVO) {
      solicitud += ` (OT: `;
      solicitud += `<span class="font-bold">${item.ORDEN_OPERACION_CONSECUTIVO}</span>`;
      solicitud += `)`;
    }

    // Añadir PREFACTURA_CONSECUTIVO si existe
    if (item.PREFACTURA_CONSECUTIVO) {
      solicitud += ` (PF: `;
      solicitud += `<span class="font-bold">${item.PREFACTURA_CONSECUTIVO}</span>`;
      solicitud += `)`;
    }

    return solicitud;
  };

  return (
    <tbody className="divide-y divide-gray-200 bg-white">
      {items && items.length > 0 ? (
        items.map((item, index) => (
          <tr
            key={`anticipo-${index}`}
            className="hover:bg-gray-50">
            <td className="w-2 p-2">
              <div className="flex items-center space-x-1">
                <ToolTipWrap
                  itemID={item.SOLICITUD_ANTICIPO_ID}
                  componentPrefix="tooltipVerAnticipo"
                  message="Ver detalles del anticipo">
                  <EyeIcon
                    data-tip
                    data-for={`tooltipVerAnticipo${item.SOLICITUD_ANTICIPO_ID}`}
                    className="h-5 w-5 cursor-pointer text-indigo-600 hover:text-indigo-900"
                    onClick={() => handleShowHistoryModal(item)}
                  />
                </ToolTipWrap>
              </div>
            </td>
            <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{item.SOLICITUD_ANTICIPO_CONSECUTIVO || 'N/A'}</td>
            <td
              className="whitespace-nowrap px-2 py-2 text-sm text-gray-900"
              dangerouslySetInnerHTML={{ __html: getFormattedSolicitud(item) }}></td>
            <td className="px-2 py-2 text-sm text-gray-900">{item.CLIENTE_NOMBRE || 'N/A'}</td>
            <td className="px-2 py-2 text-sm text-gray-900">{`${item.ORIGEN || 'N/A'} - ${item.DESTINO || 'N/A'}`}</td>
            <VehiclePlateCell item={item} />
            <BeneficiarioCell item={item} />
            <td className="whitespace-nowrap px-2 py-2 text-right text-sm text-gray-900">{formatterPeso(item.SOLICITUD_ANTICIPO_VALOR) || 'N/A'}</td>
            <td className="whitespace-nowrap px-2 py-2 text-sm text-gray-900">{formatDate(item.ANTICIPO_CREATED_AT)}</td>
            <td className="px-2 py-2 text-sm text-gray-900">{item.SOLICITUD_ANTICIPO_OBSERVACION || '-'}</td>
            <td className="whitespace-nowrap px-2 py-2 text-center">
              <SiesaLogIcon
                row={item}
                itemID={item.SOLICITUD_ANTICIPO_ID}
                consecutivoField="SOLICITUD_ANTICIPO_CONSECUTIVO"
                responseField="SOLICITUD_EXT_RESPUESTA"
                onIconClick={(row, isSendFail) => {
                  if (handleShowSiesaLog) {
                    handleShowSiesaLog(row, isSendFail);
                  } else {
                    console.log('Siesa Log clicked:', row, isSendFail);
                  }
                }}
                tooltipPrefix="tooltipSiesaLogFull"
              />
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td
            colSpan="11"
            className="px-2 py-2 text-center text-sm text-gray-500">
            No hay anticipos para mostrar
          </td>
        </tr>
      )}
    </tbody>
  );
};

export default TableAnticipoTbodyFull;
